import { ReloadOutlined } from "@ant-design/icons";
import { Button, Spin, message } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Amount, StockLink, StockPrice } from "fin-web-components";
import type { StockSearchBrief } from "../../global/GlobalType";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import { useStockLinkDetail } from "../../components/fin-web/useStockLinkDetail";
import { getEasyReadTime } from "../../utils/easyReadTime";
import { FollowPoolApi, followPoolOperationCursorTime } from "./FollowPoolApi";
import type { FollowPoolOperationRow, FollowPoolPositionRow } from "./FollowPoolType";
import { FOLLOW_POOL_OP_LABEL } from "./FollowPoolType";
import { FOLLOW_POOL_UI } from "./FollowPoolUiConfig";
import "./FollowPoolPage.css";

const OPS_PAGE_SIZE = 12;

function refChangePct(buyRefMax: number, current: number): number | null {
  if (!(buyRefMax > 0)) return null;
  return ((current - buyRefMax) / buyRefMax) * 100;
}

function opKindClass(t: number): string {
  if (t === 1 || t === 2) return "follow-pool-page__op-kind follow-pool-page__op-kind--buy";
  if (t === 3 || t === 4) return "follow-pool-page__op-kind follow-pool-page__op-kind--sell";
  return "follow-pool-page__op-kind follow-pool-page__op-kind--track";
}

/** 建仓日中文 + 持股天数（建仓日算第 1 天）；仅日期，忽略时间部分；日数用于单独上色 */
function buyDateHoldingParts(buyDateRaw?: string): { text: string; daysSuffix: string | null } {
  const s = buyDateRaw?.trim();
  if (!s) return { text: "—", daysSuffix: null };
  const head = s.split(/\s/)[0]?.trim() ?? s;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(head);
  if (!m) return { text: s, daysSuffix: null };
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (![y, mo, d].every((n) => Number.isFinite(n))) return { text: "—", daysSuffix: null };
  const buyStart = new Date(y, mo - 1, d);
  if (Number.isNaN(buyStart.getTime())) return { text: "—", daysSuffix: null };
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((todayStart.getTime() - buyStart.getTime()) / 86400000);
  const zh = `${y}年${String(mo).padStart(2, "0")}月${String(d).padStart(2, "0")}`;
  if (diffDays < 0) return { text: zh, daysSuffix: null };
  const holding = diffDays + 1;
  return { text: zh, daysSuffix: ` / ${holding}日` };
}

export interface FollowPoolPageProps {
  spaceId: string;
  spaceProductId: string;
}

export default function FollowPoolPage(props: FollowPoolPageProps): JSX.Element {
  const { spaceId, spaceProductId } = props;
  const [poolLoading, setPoolLoading] = useState(false);
  const [pool, setPool] = useState<FollowPoolPositionRow[]>([]);

  const [opsItems, setOpsItems] = useState<FollowPoolOperationRow[]>([]);
  const [opsHasMore, setOpsHasMore] = useState(false);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsLoadingMore, setOpsLoadingMore] = useState(false);
  const opsLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  /** 组合动态按股筛选；与 SearchStock 的 stockCode 一致，清空表示全部 */
  const [opsSearchStockCode, setOpsSearchStockCode] = useState<string | undefined>(undefined);

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(spaceId);

  const loadPool = useCallback(async () => {
    setPoolLoading(true);
    try {
      const pRows = await FollowPoolApi.listPool(spaceId, spaceProductId);
      if (pRows == null) {
        message.error("股票池加载失败");
        setPool([]);
      } else {
        setPool(pRows);
      }
    } finally {
      setPoolLoading(false);
    }
  }, [spaceId, spaceProductId]);

  const loadOpsFirstPage = useCallback(
    async (stockCode: string | undefined) => {
      setOpsLoading(true);
      try {
        const page = await FollowPoolApi.getLatestOperations(spaceId, spaceProductId, {
          pageSize: OPS_PAGE_SIZE,
          stockCode: stockCode?.trim() || undefined,
        });
        if (page == null) {
          message.error("组合动态加载失败");
          setOpsItems([]);
          setOpsHasMore(false);
          return;
        }
        setOpsItems(page.items ?? []);
        setOpsHasMore(Boolean(page.hasMore));
      } finally {
        setOpsLoading(false);
      }
    },
    [spaceId, spaceProductId]
  );

  const loadMoreOps = useCallback(async () => {
    if (!opsHasMore || opsLoadingMore || opsItems.length === 0) return;
    const last = opsItems[opsItems.length - 1];
    const cursorAt = followPoolOperationCursorTime(last);
    if (!cursorAt || !last.id) return;
    const stockCode = opsSearchStockCode?.trim() || undefined;
    setOpsLoadingMore(true);
    try {
      const page = await FollowPoolApi.getOperationsBefore(
        spaceId,
        spaceProductId,
        { createdAt: cursorAt, id: last.id },
        { pageSize: OPS_PAGE_SIZE, stockCode }
      );
      if (page == null) {
        message.error("加载更多失败");
        return;
      }
      setOpsItems((prev) => [...prev, ...(page.items ?? [])]);
      setOpsHasMore(Boolean(page.hasMore));
    } finally {
      setOpsLoadingMore(false);
    }
  }, [opsHasMore, opsLoadingMore, opsItems, opsSearchStockCode, spaceId, spaceProductId]);

  const onOpsStockSelect = useCallback(
    (stock: StockSearchBrief) => {
      const code = stock.code?.trim();
      if (!code) {
        return;
      }
      setOpsSearchStockCode(code);
      void loadOpsFirstPage(code);
    },
    [loadOpsFirstPage]
  );

  const onOpsSearchCleared = useCallback(() => {
    let hadFilter = false;
    setOpsSearchStockCode((prev) => {
      hadFilter = Boolean(prev?.trim());
      return hadFilter ? undefined : prev;
    });
    if (hadFilter) {
      void loadOpsFirstPage(undefined);
    }
  }, [loadOpsFirstPage]);

  useEffect(() => {
    void loadPool();
  }, [loadPool]);

  useEffect(() => {
    setOpsSearchStockCode(undefined);
    void loadOpsFirstPage(undefined);
  }, [spaceId, spaceProductId, loadOpsFirstPage]);

  useEffect(() => {
    const node = opsLoadMoreSentinelRef.current;
    if (!node || !opsHasMore || opsLoading || opsLoadingMore) return;
    const ob = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMoreOps();
      },
      { root: null, rootMargin: "160px", threshold: 0 }
    );
    ob.observe(node);
    return () => ob.disconnect();
  }, [loadMoreOps, opsHasMore, opsLoading, opsLoadingMore, opsItems.length]);

  return (
    <div
      className="follow-pool-page"
      style={
        {
          "--follow-pool-ops-row-hover-bg": FOLLOW_POOL_UI.opsRowHoverBackground,
          "--lf-fp-holding-days-color": FOLLOW_POOL_UI.holdingDaysColor,
        } as React.CSSProperties
      }
    >
      <section className="follow-pool-page__section" aria-label="跟进中的组合">
        <div className="follow-pool-page__section-head">
          <div className="follow-pool-page__section-title">跟进中的组合</div>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            className="follow-pool-page__refresh-text"
            onClick={() => {
              void loadPool();
              void loadOpsFirstPage(opsSearchStockCode?.trim() || undefined);
            }}
          >
            刷新
          </Button>
        </div>
        <Spin spinning={poolLoading}>
          <div
            className="follow-pool-page__pool-shell"
            style={{ background: FOLLOW_POOL_UI.poolShellBackground }}
          >
            <div className="follow-pool-page__pool-cards">
              {pool.length === 0 ? (
                <div className="follow-pool-page__empty">暂无在池标的</div>
              ) : (
                pool.map((row) => {
                  const pct = refChangePct(row.buyPriceMax, row.currentPrice);
                  const buyDH = buyDateHoldingParts(row.buyDate);
                  return (
                    <article key={row.code} className="follow-pool-page__pool-card">
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--col1">
                        {row.code?.trim() ? (
                          <StockLink
                            stockCode={row.code.trim()}
                            stockName={row.name?.trim() || row.code.trim()}
                            stockDetail={stockLinkDetail.code === row.code.trim() ? stockLinkDetail.data : null}
                            detailLoading={
                              stockLinkDetail.code === row.code.trim() && stockLinkDetail.loading
                            }
                            dayKlines={
                              stockLinkDetail.code === row.code.trim() ? stockLinkDetail.dayKlines : null
                            }
                            klineLoading={
                              stockLinkDetail.code === row.code.trim() && stockLinkDetail.klineLoading
                            }
                            onRequestDetail={requestStockDetailForLink}
                            stockNotesTab={buildStockNotesTabProps(
                              row.code.trim(),
                              row.name?.trim() || row.code.trim()
                            )}
                          />
                        ) : (
                          <span className="follow-pool-page__pool-name">{row.name ?? "—"}</span>
                        )}
                      </div>
                      <div className="follow-pool-page__pool-gc">
                        <span className="follow-pool-page__pool-k">参考买入</span>
                      </div>
                      <div className="follow-pool-page__pool-gc">
                        <span className="follow-pool-page__pool-k">建议仓位</span>
                      </div>
                      <div className="follow-pool-page__pool-gc">
                        <span className="follow-pool-page__pool-k">买入区间</span>
                      </div>
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--col5">
                        <span className="follow-pool-page__pool-k">建仓日/持股天数</span>
                      </div>
                      <div
                        className="follow-pool-page__pool-gc follow-pool-page__pool-gc--col1 follow-pool-page__pool-gc--quote follow-pool-page__pool-gc--row2"
                      >
                        <StockPrice
                          price={row.currentPrice}
                          change={pct != null && Number.isFinite(pct) ? pct : Number.NaN}
                          fontSize={12}
                        />
                      </div>
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--row2">
                        <span className="follow-pool-page__pool-v">
                          <Amount value={row.buyPriceMax} fontSize={14} fontWeight={300} />
                        </span>
                      </div>
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--row2">
                        <span className="follow-pool-page__pool-v">
                          {row.suggestedPositionPct == null ? (
                            "—"
                          ) : (
                            <span className="follow-pool-page__fin-num">
                              {row.suggestedPositionPct.toFixed(1)}%
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--row2">
                        <span className="follow-pool-page__pool-v follow-pool-page__pool-v--range">
                          {row.buyRangeLow == null && row.buyRangeHigh == null ? (
                            "—"
                          ) : (
                            <>
                              <Amount value={row.buyRangeLow} fontSize={14} fontWeight={300} />
                              <span className="follow-pool-page__range-sep">-</span>
                              <Amount value={row.buyRangeHigh} fontSize={14} fontWeight={300} />
                            </>
                          )}
                        </span>
                      </div>
                      <div className="follow-pool-page__pool-gc follow-pool-page__pool-gc--col5 follow-pool-page__pool-gc--row2">
                        <span className="follow-pool-page__pool-v">
                          {buyDH.text}
                          {buyDH.daysSuffix ? (
                            <span className="follow-pool-page__pool-holding-days">{buyDH.daysSuffix}</span>
                          ) : null}
                        </span>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </Spin>
      </section>

      <section className="follow-pool-page__section follow-pool-page__section--ops" aria-label="组合动态">
        <div className="follow-pool-page__section-head follow-pool-page__section-head--ops">
          <div className="follow-pool-page__section-title">组合动态</div>
          <div className="follow-pool-page__ops-toolbar">
            <SearchStockWithCatalog
              stockCode={opsSearchStockCode}
              onSelect={onOpsStockSelect}
              onCleared={onOpsSearchCleared}
              placeholder="搜索查看特定股票动态"
              borderRadius={5}
              backgroundColor="#ffffff"
              focusBackgroundColor="#ffffff"
            />
          </div>
        </div>
        <Spin spinning={opsLoading}>
          <div className="follow-pool-page__op-list">
            {opsItems.length === 0 && !opsLoading ? (
              <div className="follow-pool-page__empty follow-pool-page__empty--inline">暂无动态</div>
            ) : (
              opsItems.map((row) => {
                const typeNum = row.type as number;
                const label =
                  FOLLOW_POOL_OP_LABEL[row.type as keyof typeof FOLLOW_POOL_OP_LABEL] ?? `类型${typeNum}`;
                const sc = row.stock?.code?.trim() ?? "";
                const sn = row.stock?.name?.trim() ?? "";

                let priceBlock: React.ReactNode = null;
                let ratioBlock: React.ReactNode = null;
                if (typeNum === 1 || typeNum === 2) {
                  const hasBuyRange = row.priceLow != null || row.priceHigh != null;
                  priceBlock = (
                    <span className="follow-pool-page__op-line1-piece">
                      <span className="follow-pool-page__op-field-label">建议价格</span>
                      <span className="follow-pool-page__op-field-value">
                        {hasBuyRange ? (
                          <>
                            <Amount value={row.priceLow} fontSize={14} fontWeight={300} />
                            <span className="follow-pool-page__range-sep">-</span>
                            <Amount value={row.priceHigh} fontSize={14} fontWeight={300} />
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </span>
                  );
                  ratioBlock =
                    row.suggestedPositionPct != null ? (
                      <span className="follow-pool-page__op-line1-piece">
                        <span className="follow-pool-page__op-field-label">建议仓位</span>
                        <span className="follow-pool-page__op-field-value">
                          <span className="follow-pool-page__fin-num">
                            {row.suggestedPositionPct.toFixed(1)}%
                          </span>
                        </span>
                      </span>
                    ) : null;
                } else if (typeNum === 3 || typeNum === 4) {
                  priceBlock = (
                    <span className="follow-pool-page__op-line1-piece">
                      <span className="follow-pool-page__op-field-label">卖出价格</span>
                      <span className="follow-pool-page__op-field-value">
                        {row.priceLow != null && Number.isFinite(row.priceLow) ? (
                          <>
                            ≥ <Amount value={row.priceLow} fontSize={14} fontWeight={300} />
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </span>
                  );
                  ratioBlock =
                    row.sellRatioPct != null ? (
                      <span className="follow-pool-page__op-line1-piece">
                        <span className="follow-pool-page__op-field-label">卖出比例</span>
                        <span className="follow-pool-page__op-field-value">
                          {row.sellRatioPct.toFixed(1)}%
                        </span>
                      </span>
                    ) : null;
                  if (row.sellCostPrice != null && Number.isFinite(row.sellCostPrice)) {
                    const prev = ratioBlock;
                    ratioBlock = (
                      <>
                        {prev}
                        <span className="follow-pool-page__op-line1-piece">
                          <span className="follow-pool-page__op-field-label">参考成本</span>
                          <span className="follow-pool-page__op-field-value">
                            <Amount value={row.sellCostPrice} fontSize={14} fontWeight={300} />
                          </span>
                        </span>
                      </>
                    );
                  }
                  if (
                    row.sellPortfolioPct != null &&
                    Number.isFinite(row.sellPortfolioPct)
                  ) {
                    const prev = ratioBlock;
                    ratioBlock = (
                      <>
                        {prev}
                        <span className="follow-pool-page__op-line1-piece">
                          <span className="follow-pool-page__op-field-label">组合仓位变动</span>
                          <span className="follow-pool-page__op-field-value">
                            {row.sellPortfolioPct.toFixed(2)}%
                          </span>
                        </span>
                      </>
                    );
                  }
                }

                return (
                  <div key={row.id} className="follow-pool-page__op-row">
                    <div className="follow-pool-page__op-line1">
                      <div className="follow-pool-page__op-line1-main">
                        <span className={opKindClass(typeNum)}>{label}</span>
                        {sc ? (
                          <span className="follow-pool-page__op-stock-inline">
                            <StockLink
                              stockCode={sc}
                              stockName={sn || sc}
                              stockDetail={stockLinkDetail.code === sc ? stockLinkDetail.data : null}
                              detailLoading={stockLinkDetail.code === sc && stockLinkDetail.loading}
                              dayKlines={stockLinkDetail.code === sc ? stockLinkDetail.dayKlines : null}
                              klineLoading={stockLinkDetail.code === sc && stockLinkDetail.klineLoading}
                              onRequestDetail={requestStockDetailForLink}
                              stockNotesTab={buildStockNotesTabProps(sc, sn || sc)}
                            />
                          </span>
                        ) : (
                          <span className="follow-pool-page__op-stock-inline">—</span>
                        )}
                        {priceBlock}
                        {ratioBlock}
                      </div>
                      <div className="follow-pool-page__op-line1-time">
                        {getEasyReadTime(row.createdAt)}
                      </div>
                    </div>
                    <p className="follow-pool-page__op-body">{row.interpretation}</p>
                  </div>
                );
              })
            )}
            {opsHasMore ? <div ref={opsLoadMoreSentinelRef} className="follow-pool-page__ops-sentinel" /> : null}
            {opsLoadingMore ? <div className="follow-pool-page__ops-more">加载中…</div> : null}
            {!opsHasMore && opsItems.length > 0 ? <div className="follow-pool-page__ops-end">没有更多了</div> : null}
          </div>
        </Spin>
      </section>
    </div>
  );
}
